import { OrganizationColumn } from '@/features/behavior-config';
import Table from '@oncolens/oncolens-ui/lib/components/Common/Table';
import { ComboboxItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import React, { useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import { getGuidelines, GuidelineDto, useDeleteGuideline } from '../api/guidelines';
import ActionsColumn from './ActionsColumn';

interface Props {
  searchQuery: string;
  orgFilter: ComboboxItem[];
}

const GuidelinesTable = ({ searchQuery, orgFilter }: Props) => {
  const columns = React.useMemo(
    () => [
      { accessor: 'actions', Header: '', Cell: GuidelineActionsColumn, stopClickPropagation: true },
      { accessor: 'name', Header: 'File Name' },
      { accessor: 'organization', Header: 'Organizations', Cell: GuidelinesOrganizationColumn },
      {
        accessor: 'fileLastUpdated',
        Header: 'Last Upload Date',
        Cell: DateColumn,
      },
    ],
    [],
  );

  const filters = {
    Filters: [...(searchQuery ? [`(name)@=*${searchQuery}`] : [])].join(','),
    ...(orgFilter && orgFilter.length
      ? { organizations: orgFilter.map((item) => item.value).join('|') }
      : {}),
  };

  const initialSort = useMemo(() => [{ id: 'fileLastUpdated', desc: true }], []);

  const navigate = useNavigate();
  const navigateToEdit = (rowData: any) =>
    navigate(`/behavior-config/guidelines/guideline/${rowData.id}`);

  return (
    <Table
      columns={columns}
      onRowClick={navigateToEdit}
      fetchData={getGuidelines as () => Promise<any>}
      queryBaseKey="guidelines"
      additionalQueryParameters={filters}
      options={{ textSize: 'sm' }}
      initialSort={initialSort}
    />
  );
};

interface RowProps {
  row: {
    original: GuidelineDto;
  };
}

const GuidelinesOrganizationColumn = ({ row }: RowProps) => <OrganizationColumn row={row} />;

const GuidelineActionsColumn = ({ row }: RowProps) => {
  const { original: data } = row;
  const deleteGuideline = useDeleteGuideline();
  const onDelete = (toggle: () => void) =>
    deleteGuideline.mutateAsync(data.id).then(() => toggle());

  return (
    <ActionsColumn
      onDelete={onDelete}
      modalTitle="Delete Guideline"
      modalContent="Are you sure you want to delete this guideline?"
      deleteItemTestSelector="guideline-delete-button"
      deleteModalTestSelector="delete-guideline-modal"
      deleteItemConfirmationTestSelector="delete-guideline-modal-confirm"
    />
  );
};

const DateColumn = ({ row: { original: rowData } }: RowProps) => (
  <div className="flex flex-col space-y-1">{rowData.fileLastUpdated.toLocaleString()}</div>
);

export { GuidelinesTable as default };
